import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://widget.docsbot.ai/chat.js";
const HIDE_STYLE_ID = "docsbot-hide-style";

// load the script once
function loadScriptOnce() {
  return new Promise((resolve, reject) => {
    if (window.__docsbotScriptLoaded) return resolve();
    // check if the script is already loaded
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);

    // on load, set the script loaded flag and resolve the promise
    const onLoad = () => {
      window.__docsbotScriptLoaded = true;
      resolve();
    };

    // on error, reject the promise
    const onError = () => reject(new Error("Failed to load DocsBot script"));

    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });

      // if the DocsBotAI object is already loaded, set the script loaded flag and resolve the promise
      if (window.DocsBotAI) {
        window.__docsbotScriptLoaded = true;
        resolve();
      }
      return;
    }

    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.addEventListener("load", onLoad, { once: true });
    s.addEventListener("error", onError, { once: true });
    document.head.appendChild(s);
  });
}

// set the hidden style
function setHidden(hidden) {
  let styleEl = document.getElementById(HIDE_STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = HIDE_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = hidden
    ? `.docsbot-launcher, .docsbot-chat-container { display: none !important; }`
    : "";
}

// try to destroy the DocsBotAI widget
async function tryDestroy() {
  const api = window.DocsBotAI;
  try {
    if (api?.unmount) {
      await api.unmount();
      return;
    }
    if (api?.destroy) {
      await api.destroy();
      return;
    }
  } catch (_) {
    // ignore the error if the widget is not destroyed
    // ignore
  }
  // remove the widget from the DOM
  document
    .querySelectorAll(".docsbot-launcher, .docsbot-chat-container")
    .forEach((n) => n.remove());
}

// the main component that mounts the DocsBotAI widget
export default function DocsBotWidget({
  id,
  lang,
  enabled = true,
  options,
  metadata,
  reloadKey, // use this externally as reloadKey={lang}
}) {
  const mountedRef = useRef(false); // reference to track if the widget is mounted
  const lastReloadKeyRef = useRef();

  useEffect(() => {
    let cancelled = false; // flag to track if the widget is cancelled

    (async () => {
      await loadScriptOnce();
      if (cancelled) return;

    // be sure to destroy the widget if it is not enabled
      if (!enabled) {
        if (mountedRef.current) {
            await tryDestroy();
            mountedRef.current = false;
        }
        lastReloadKeyRef.current = reloadKey;
        return;
      }

      // remount when reloadKey changes (e.g. when lang changes)
      const needRemount =
        mountedRef.current && lastReloadKeyRef.current !== reloadKey;

      // destroy the widget if it needs to be remounted
      if (needRemount) {
        await tryDestroy();
        mountedRef.current = false;
      }

      // mount the widget if it is not mounted
      if (!mountedRef.current && window.DocsBotAI?.mount) {
        await window.DocsBotAI.mount({
          id,
          options: { ...(options || {}) },
          metadata: {
            path: window.location.pathname,
            lang,
            ...(metadata || {}),
          },
        });
        mountedRef.current = true;
      }

      lastReloadKeyRef.current = reloadKey;
    })();

    return () => {
      cancelled = true;
    };
  }, [id, lang, enabled, options, metadata, reloadKey]);

  return null;
}
